// Admin catalog screen (admin console screen 2 of 5) — route /products
// (deliberately not /catalog or /admin/catalog: both collide with the dev
// proxy's/Nginx's API path prefixes, see components/AdminNav.jsx's own
// header comment and this task's brief). Products, create/edit, and
// per-tier list/floor prices. Ledger-style: one ruled table, no sidebar,
// no data-grid library (root CLAUDE.md, admin/CLAUDE.md) — same shape as
// AllRequestsPage/ApprovalQueuePage.
//
// Four tiers per product is a lot of columns for one row, so each row shows
// a compact "N/4 tiers priced" summary with an expand toggle
// (PriceTierPanel.jsx) rather than 8 price columns — same "collapsed by
// default, expand near the trigger" language as Collapsible.jsx/
// PriceReference.jsx's "other tiers" toggle.
//
// Cost boundary (root CLAUDE.md rule #1 / this task's own scope note): this
// screen never reads cost_price/supplier_name/cost_updated_at anywhere —
// see hooks/useAdminCatalog.js's own header comment. Cost management is a
// SEPARATE screen (#3, not built by this task), reachable from AdminNav's
// own "Cost management" item, never from here.
//
// Deactivate/reactivate/delete, single-row and bulk (customers/products/
// users multi-select task): a "show inactive too" checkbox opts into
// includeInactive=1 on the list read so a deactivated row can be found
// again and reactivated — default off, matching this screen's prior
// active-only behavior when nobody needs to see deactivated rows. Bulk
// delete decides delete-vs-deactivate server-side per product (referenced
// by a request/quotation/audit entry -> deactivate, never hard delete —
// root CLAUDE.md's non-negotiable rule) and previews that split, with
// counts, before the admin confirms — see utils/bulkActions.js and
// components/BulkActionBar.jsx, shared with CustomersPage/UsersPage.
//
// Duplicate and feature/unfeature (catalog-restructure follow-up): both
// fold into the existing actions cell rather than adding a dedicated
// "Featured" column — this task's own brief flagged that as a real,
// measured 1366px width constraint other screens in this codebase have hit
// before, verified here by actually screenshotting (see PROGRESS.md-style
// verification in this task's final report). Duplicate is a single-click
// action behind a confirm() dialog (useActionFeedback's own promise-based
// confirm, same pattern as handleDeactivate below) — there is nothing left
// to type (the duplicate's SKU is always server-generated, see
// hooks/useAdminCatalog.js's own useDuplicateProduct() comment), so the
// previous inline mini-form is gone.

import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useAdminCatalogProducts,
  useAdminCategories,
  useCreateProduct,
  useUpdateProduct,
  useDeactivateProduct,
  useReactivateProduct,
  useDuplicateProduct,
  useSetProductFeatured,
  useBulkDeactivateProducts,
  useBulkReactivateProducts,
  useBulkDeleteProducts,
  fetchProductBulkDeletePreview,
} from '../hooks/useAdminCatalog.js';
import { useRowSelection } from '../hooks/useRowSelection.js';
import { confirmAndBulkAction, confirmAndBulkDelete } from '../utils/bulkActions.js';
import { useDebouncedValue } from '../utils/useDebouncedValue.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import ProductForm from '../components/ProductForm.jsx';
import PriceTierPanel from '../components/PriceTierPanel.jsx';
import BulkActionBar from '../components/BulkActionBar.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './CatalogPage.module.css';

const TIER_COUNT = 4;
// checkbox, sku, name, category, condition, prices, actions
const TABLE_COLUMN_COUNT = 7;

function categoryLabel(categoryId, categories, lang, t) {
  if (!categoryId) return t('catalog.categoryNone');
  const cat = (categories || []).find((c) => c.id === categoryId);
  if (!cat) return t('catalog.categoryNone');
  return lang === 'am' && cat.name_am ? cat.name_am : cat.name;
}

// 'refurbished_manufacturer' -> 'refurbishedManufacturer', matching
// ProductForm.jsx's own identical helper and shared/i18n/strings.js's
// catalog.condition.* key-naming convention.
function conditionLabel(condition, t) {
  if (!condition) return '';
  const segment = condition.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return t(`catalog.condition.${segment}`);
}

function ProductRow({ product, categories, selection }) {
  const { t, lang } = useLanguage();
  const [pricesOpen, setPricesOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [featuredOrderInput, setFeaturedOrderInput] = useState(
    product.featuredOrder != null ? String(product.featuredOrder) : ''
  );

  const updateMutation = useUpdateProduct();
  const deactivateMutation = useDeactivateProduct();
  const reactivateMutation = useReactivateProduct();
  const duplicateMutation = useDuplicateProduct();
  const featureMutation = useSetProductFeatured();
  const { confirm, toastOk, toastErr } = useActionFeedback();

  // Keeps the inline order input in sync with the cache after a successful
  // feature/order save (or any other refetch) — only runs when the
  // underlying value actually changes, so it doesn't fight the admin while
  // they're still typing in the field.
  useEffect(() => {
    setFeaturedOrderInput(product.featuredOrder != null ? String(product.featuredOrder) : '');
  }, [product.featuredOrder]);

  const displayName = lang === 'am' && product.nameAm ? product.nameAm : product.name;
  const pricedCount = product.tiers.length;

  const handleUpdate = (body) => {
    updateMutation.mutate(
      { id: product.id, body },
      {
        onSuccess: () => {
          setEditing(false);
          toastOk(t('catalog.product.updatedToast'));
        },
        onError: (err) => toastErr(err, ['DUPLICATE_SKU', 'VALIDATION_ERROR', 'PRODUCT_NOT_FOUND']),
      }
    );
  };

  const handleDeactivate = async () => {
    const ok = await confirm({
      title: t('catalog.actions.deactivateConfirmTitle', { name: displayName }),
      message: t('catalog.actions.deactivateConfirmBody'),
      confirmLabel: t('catalog.actions.deactivateConfirm'),
      tone: 'danger',
    });
    if (!ok) return;
    deactivateMutation.mutate(product.id, {
      onSuccess: () => toastOk(t('catalog.product.deactivatedToast')),
      onError: (err) => toastErr(err, ['PRODUCT_NOT_FOUND']),
    });
  };

  const handleReactivate = () => {
    reactivateMutation.mutate(product.id, {
      onSuccess: () => toastOk(t('catalog.product.reactivatedToast')),
      onError: (err) => toastErr(err, ['PRODUCT_NOT_FOUND', 'PRODUCT_ALREADY_ACTIVE']),
    });
  };

  const handleDuplicate = async () => {
    const ok = await confirm({
      title: t('catalog.actions.duplicateConfirmTitle', { name: displayName }),
      message: t('catalog.actions.duplicateConfirmBody'),
      confirmLabel: t('catalog.actions.duplicateConfirm'),
    });
    if (!ok) return;
    duplicateMutation.mutate(
      { id: product.id },
      {
        onSuccess: (data) => {
          const sku = data?.product?.sku;
          toastOk(
            sku
              ? t('catalog.product.duplicatedWithSkuToast', { sku })
              : t('catalog.product.duplicatedToast')
          );
        },
        onError: (err) => toastErr(err, ['VALIDATION_ERROR', 'PRODUCT_NOT_FOUND']),
      }
    );
  };

  const handleFeature = () => {
    featureMutation.mutate(
      { id: product.id, isFeatured: true },
      {
        onSuccess: () => toastOk(t('catalog.product.featuredToast')),
        onError: (err) => toastErr(err, ['FEATURED_LIMIT_REACHED', 'PRODUCT_NOT_FOUND']),
      }
    );
  };

  const handleUnfeature = () => {
    featureMutation.mutate(
      { id: product.id, isFeatured: false },
      {
        onSuccess: () => toastOk(t('catalog.product.unfeaturedToast')),
        onError: (err) => toastErr(err, ['PRODUCT_NOT_FOUND']),
      }
    );
  };

  const handleFeaturedOrderBlur = () => {
    const trimmed = featuredOrderInput.trim();
    const n = Number(trimmed);
    if (!trimmed || !Number.isInteger(n) || n <= 0 || n === product.featuredOrder) return;
    featureMutation.mutate(
      { id: product.id, isFeatured: true, featuredOrder: n },
      {
        onSuccess: () => toastOk(t('catalog.product.featuredOrderSavedToast')),
        onError: (err) => toastErr(err, ['FEATURED_LIMIT_REACHED', 'PRODUCT_NOT_FOUND']),
      }
    );
  };

  return (
    <>
      <tr className={`${styles.row} ${!product.isActive ? styles.rowInactive : ''}`}>
        <td className={styles.checkboxCell}>
          <input
            type="checkbox"
            checked={selection.isSelected(product.id)}
            onChange={() => selection.toggle(product.id)}
            aria-label={displayName}
          />
        </td>
        <td className={`${styles.skuCell} tabularNums`}>{product.sku}</td>
        <td>{displayName}</td>
        <td>{categoryLabel(product.categoryId, categories, lang, t)}</td>
        <td>{conditionLabel(product.condition, t)}</td>
        <td>
          <button
            type="button"
            className={styles.pricesToggle}
            onClick={() => setPricesOpen((prev) => !prev)}
            aria-expanded={pricesOpen}
          >
            <span className="tabularNums">
              {t('catalog.tiers.pricedCount', { priced: pricedCount, total: TIER_COUNT })}
            </span>{' '}
            · {pricesOpen ? t('catalog.tiers.toggleHide') : t('catalog.tiers.toggleShow')}
          </button>
        </td>
        <td className={styles.actionsCell}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setEditing((prev) => !prev)}
          >
            {t('catalog.actions.edit')}
          </button>
          <button
            type="button"
            className={styles.linkButton}
            onClick={handleDuplicate}
            disabled={duplicateMutation.isPending}
          >
            {duplicateMutation.isPending && <Spinner />}{' '}
            {duplicateMutation.isPending
              ? t('catalog.actions.duplicating')
              : t('catalog.actions.duplicate')}
          </button>
          {product.isFeatured ? (
            <span className={styles.featuredGroup}>
              <span
                className={styles.featuredBadge}
                title={t('catalog.actions.featuredOrderLabel')}
              >
                ★
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={featuredOrderInput}
                  onChange={(e) => setFeaturedOrderInput(e.target.value)}
                  onBlur={handleFeaturedOrderBlur}
                  aria-label={t('catalog.actions.featuredOrderLabel')}
                  className={`${styles.featuredOrderInput} tabularNums`}
                />
              </span>
              <button
                type="button"
                className={styles.linkButton}
                onClick={handleUnfeature}
                disabled={featureMutation.isPending}
              >
                {t('catalog.actions.unfeature')}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className={styles.linkButton}
              onClick={handleFeature}
              disabled={featureMutation.isPending}
            >
              {t('catalog.actions.feature')}
            </button>
          )}
          {product.isActive ? (
            <button
              type="button"
              className={styles.deactivateLink}
              onClick={handleDeactivate}
              disabled={deactivateMutation.isPending}
            >
              {deactivateMutation.isPending && <Spinner />}{' '}
              {deactivateMutation.isPending
                ? t('catalog.actions.deactivating')
                : t('catalog.actions.deactivate')}
            </button>
          ) : (
            <button
              type="button"
              className={styles.linkButton}
              onClick={handleReactivate}
              disabled={reactivateMutation.isPending}
            >
              {reactivateMutation.isPending && <Spinner />}{' '}
              {reactivateMutation.isPending
                ? t('catalog.actions.reactivating')
                : t('catalog.actions.reactivate')}
            </button>
          )}
        </td>
      </tr>

      {editing && (
        <tr>
          <td colSpan={TABLE_COLUMN_COUNT} className={styles.expandedCell}>
            <ProductForm
              mode="edit"
              product={product}
              categories={categories}
              onSubmit={handleUpdate}
              onCancel={() => setEditing(false)}
              isPending={updateMutation.isPending}
            />
          </td>
        </tr>
      )}

      {pricesOpen && (
        <tr>
          <td colSpan={TABLE_COLUMN_COUNT} className={styles.expandedCell}>
            <PriceTierPanel productId={product.id} tiers={product.tiers} />
          </td>
        </tr>
      )}
    </>
  );
}

export default function CatalogPage() {
  const { t, lang } = useLanguage();
  const [searchInput, setSearchInput] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const debouncedSearch = useDebouncedValue(searchInput, 250);
  const categoryId = categoryFilter ? Number(categoryFilter) : undefined;
  const search = debouncedSearch.trim() || undefined;

  const {
    data: products,
    isLoading,
    isError,
    error,
    refetch,
  } = useAdminCatalogProducts({ categoryId, search, includeInactive: showInactive });
  const { data: categories } = useAdminCategories();
  const createMutation = useCreateProduct();
  const { confirm, toastOk, toastErr } = useActionFeedback();

  const isFiltered = Boolean(categoryId || search);
  const allProducts = products || [];

  const isTrulyEmpty = !isLoading && !isError && allProducts.length === 0 && !isFiltered;
  const isFilteredEmpty = !isLoading && !isError && allProducts.length === 0 && isFiltered;

  const selection = useRowSelection(allProducts);
  const bulkDeactivate = useBulkDeactivateProducts();
  const bulkReactivate = useBulkReactivateProducts();
  const bulkDelete = useBulkDeleteProducts();

  const handleBulkDeactivate = () =>
    confirmAndBulkAction({
      ids: selection.selectedIds,
      confirm,
      t,
      titleId: 'bulk.deactivateConfirmTitle',
      messageId: 'bulk.deactivateConfirmBody',
      confirmLabelId: 'bulk.deactivateConfirmButton',
      actionFn: (ids) => bulkDeactivate.mutateAsync(ids),
      toastOk,
      toastErr,
      onSettled: () => selection.clear(),
    });

  const handleBulkReactivate = () =>
    confirmAndBulkAction({
      ids: selection.selectedIds,
      confirm,
      t,
      titleId: 'bulk.reactivateConfirmTitle',
      messageId: 'bulk.reactivateConfirmBody',
      confirmLabelId: 'bulk.reactivateConfirmButton',
      actionFn: (ids) => bulkReactivate.mutateAsync(ids),
      toastOk,
      toastErr,
      onSettled: () => selection.clear(),
    });

  const handleBulkDelete = () =>
    confirmAndBulkDelete({
      ids: selection.selectedIds,
      confirm,
      t,
      previewFn: fetchProductBulkDeletePreview,
      deleteFn: (ids, confirmedCount) => bulkDelete.mutateAsync({ ids, confirmedCount }),
      toastOk,
      toastErr,
      onSettled: () => selection.clear(),
    });

  const handleCreate = (body) => {
    createMutation.mutate(body, {
      onSuccess: (data) => {
        setShowCreateForm(false);
        const sku = data?.product?.sku;
        toastOk(
          sku
            ? t('catalog.product.createdWithSkuToast', { sku })
            : t('catalog.product.createdToast')
        );
      },
      onError: (err) => toastErr(err, ['DUPLICATE_SKU', 'VALIDATION_ERROR']),
    });
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('catalog.title')}</h1>
      <p className={styles.subtitle}>{t('catalog.subtitle')}</p>

      <div className={styles.filterRow}>
        <label className={styles.filterLabel} htmlFor="catalog-categoryFilter">
          {t('catalog.filter.categoryLabel')}
        </label>
        <select
          id="catalog-categoryFilter"
          className={styles.filterSelect}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">{t('catalog.filter.categoryAll')}</option>
          {(categories || []).map((cat) => (
            <option key={cat.id} value={cat.id}>
              {lang === 'am' && cat.name_am ? cat.name_am : cat.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          className={styles.searchInput}
          placeholder={t('catalog.filter.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <label className={styles.inactiveToggle}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          {t('catalog.filter.showInactive')}
        </label>

        <button
          type="button"
          className={styles.newProductButton}
          onClick={() => setShowCreateForm((prev) => !prev)}
        >
          {t('catalog.newProduct')}
        </button>
      </div>

      {showCreateForm && (
        <ProductForm
          mode="create"
          categories={categories}
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
          isPending={createMutation.isPending}
        />
      )}

      <BulkActionBar
        count={selection.count}
        onClear={selection.clear}
        actions={[
          {
            id: 'deactivate',
            labelId: 'bulk.deactivate',
            onClick: handleBulkDeactivate,
            pending: bulkDeactivate.isPending,
          },
          {
            id: 'reactivate',
            labelId: 'bulk.reactivate',
            onClick: handleBulkReactivate,
            pending: bulkReactivate.isPending,
          },
          {
            id: 'delete',
            labelId: 'bulk.delete',
            tone: 'danger',
            onClick: handleBulkDelete,
            pending: bulkDelete.isPending,
          },
        ]}
      />

      {isLoading && <LoadingState variant="rows" rows={6} />}

      {isError && !isLoading && (
        <ErrorState messageId="catalog.errorBody" error={error} onRetry={refetch} />
      )}

      {isTrulyEmpty && <EmptyState titleId="catalog.emptyTitle" bodyId="catalog.emptyBody" />}

      {isFilteredEmpty && (
        <EmptyState titleId="catalog.emptyFilteredTitle" bodyId="catalog.emptyFilteredBody" />
      )}

      {!isLoading && !isError && allProducts.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  aria-label={t('bulk.selectAll')}
                  checked={selection.allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = selection.someSelected;
                  }}
                  onChange={selection.toggleAll}
                />
              </th>
              <th>{t('catalog.col.sku')}</th>
              <th>{t('catalog.col.name')}</th>
              <th>{t('catalog.col.category')}</th>
              <th>{t('catalog.col.condition')}</th>
              <th>{t('catalog.col.prices')}</th>
              <th>{t('catalog.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {allProducts.map((product) => (
              <ProductRow
                key={product.id}
                product={product}
                categories={categories}
                selection={selection}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
